import { hasConsent } from '@/lib/consent';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getSessionId } from './useSessionTracking';
import { isDoNotTrack } from '@/lib/doNotTrack';
import { isAppIdle } from '@/hooks/useAfterIdle';

export type DecisionReason = 'source' | 'device' | 'time' | 'manual' | 'redirect' | 'navigation';

const isPulseTrackingEnabled = (): boolean => {
  // Browser-side pulse tracking is disabled by default because common ad
  // blockers block supabase.co/functions/v1/pulse and Lighthouse reports that
  // as a console error. Re-enable only in a deliberate debug session.
  if (!hasConsent('statistics')) return false;
  return sessionStorage.getItem('enable-pulse-tracking') === 'true';
};

const TRACK_EVENT_URL = `${import.meta.env.VITE_SUPABASE_URL ?? ''}/functions/v1/pulse`;

/**
 * Send a tracking payload via the lightest available transport.
 *
 * `sendBeacon` is preferred because it is non-blocking, queues even
 * during unload, and avoids loading the auth-js bundle that
 * `supabase.functions.invoke` would pull in on the critical path.
 */
const sendBeaconEvent = (payload: object): boolean => {
  if (!isPulseTrackingEnabled()) return true;
  if (!TRACK_EVENT_URL) return false;
  try {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    return navigator.sendBeacon(TRACK_EVENT_URL, blob);
  } catch {
    return false;
  }
};

const runWhenIdle = (cb: () => void) => {
  if (isAppIdle()) {
    cb();
    return;
  }
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
  if (typeof ric === 'function') {
    ric(cb, { timeout: 3000 });
  } else {
    setTimeout(cb, 1500);
  }
};

/**
 * Sends a page_view event to the tracking endpoint
 */
const sendPageViewEvent = async (
  sessionId: string,
  pagePath: string,
  pageTitle: string,
  timeOnPage?: number
): Promise<void> => {
  // Skip if Do-Not-Track is enabled
  if (isDoNotTrack()) return;
  
  const payload: Record<string, unknown> = {
    event_type: 'page_view',
    session_id: sessionId,
    timestamp: new Date().toISOString(),
    payload: {
      page_path: pagePath,
      page_title: pageTitle,
      ...(timeOnPage !== undefined && { time_on_page: timeOnPage }),
    },
  };

  // Prefer sendBeacon (non-blocking, off-main-thread).
  if (sendBeaconEvent(payload)) return;
  // Fallback to fetch (still avoids the auth-js bundle).
  try {
    await fetch(TRACK_EVENT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Silently swallow — ad-blockers (uBlock, Brave, etc.) routinely block
    // tracking endpoints and the resulting console noise is not actionable.
  }
};

/**
 * Sends a route_decision event to the tracking endpoint
 */
export const trackRouteDecision = async (
  fromPage: string,
  toPage: string,
  decisionReason: DecisionReason
): Promise<void> => {
  // Skip if Do-Not-Track is enabled
  if (isDoNotTrack()) return;
  
  const sessionId = getSessionId();
  
  const payload = {
    event_type: 'route_decision',
    session_id: sessionId,
    timestamp: new Date().toISOString(),
    payload: {
      from_page: fromPage,
      to_page: toPage,
      decision_reason: decisionReason,
    },
  };

  if (sendBeaconEvent(payload)) return;
  try {
    await fetch(TRACK_EVENT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Silent: ad-blocker / network failure is expected here.
  }
};

/**
 * Sends a route_decision event using sendBeacon (for redirects/unload)
 */
export const trackRouteDecisionBeacon = (
  fromPage: string,
  toPage: string,
  decisionReason: DecisionReason
): void => {
  // Skip if Do-Not-Track is enabled
  if (isDoNotTrack()) return;
  if (!isPulseTrackingEnabled()) return;
  
  const sessionId = getSessionId();
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  if (!supabaseUrl) return;

  const payload = JSON.stringify({
    event_type: 'route_decision',
    session_id: sessionId,
    timestamp: new Date().toISOString(),
    payload: {
      from_page: fromPage,
      to_page: toPage,
      decision_reason: decisionReason,
    },
  });

  navigator.sendBeacon(
    `${supabaseUrl}/functions/v1/pulse`,
    new Blob([payload], { type: 'application/json' })
  );
};

/**
 * Hook to track page views with time-on-page measurement
 * Fires page_view and route_decision events on each route change
 */
export const usePageTracking = () => {
  const location = useLocation();
  const sessionId = getSessionId();
  const pageEntryTime = useRef<number>(Date.now());
  const lastPath = useRef<string>(location.pathname);
  const lastTitle = useRef<string>(document.title);
  const isFirstLoad = useRef<boolean>(true);

  useEffect(() => {
    // Calculate time spent on previous page
    const timeOnPreviousPage = Date.now() - pageEntryTime.current;
    const previousPath = lastPath.current;
    const currentPath = location.pathname;
    
    // Only process if path actually changed
    if (previousPath !== currentPath) {
      // Send time_on_page for previous page (if not first load)
      if (!isFirstLoad.current && timeOnPreviousPage > 0) {
        sendPageViewEvent(
          sessionId,
          previousPath,
          lastTitle.current,
          Math.round(timeOnPreviousPage / 1000)
        );
        
        // Fire route_decision event for navigation
        trackRouteDecision(previousPath, currentPath, 'navigation');
      }
      
      isFirstLoad.current = false;
    }

    // Reset entry time for new page
    pageEntryTime.current = Date.now();
    lastPath.current = currentPath;
    
    // Defer the initial page_view event until the browser is idle so it
    // never lands on the LCP critical path.
    runWhenIdle(() => {
      lastTitle.current = document.title;
      // Send initial page_view event (without time_on_page)
      sendPageViewEvent(sessionId, currentPath, document.title);

      // Pinterest pagevisit tracking
      if (!isDoNotTrack() && hasConsent('marketing') && typeof window !== 'undefined' && (window as any).pintrk) {
        (window as any).pintrk('track', 'pagevisit', {
          event_id: `pv_${sessionId}_${Date.now()}`
        });
      }
    });

  }, [location.pathname, sessionId]);

  // Handle page unload - send final time_on_page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!isPulseTrackingEnabled()) return;

      const timeOnPage = Math.round((Date.now() - pageEntryTime.current) / 1000);
      
      // Use sendBeacon for reliable delivery on page unload
      const payload = JSON.stringify({
        event_type: 'page_view',
        session_id: sessionId,
        timestamp: new Date().toISOString(),
        payload: {
          page_path: location.pathname,
          page_title: document.title,
          time_on_page: timeOnPage,
          is_exit: true,
        },
      });

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      
      if (supabaseUrl) {
        navigator.sendBeacon(
          `${supabaseUrl}/functions/v1/pulse`,
          new Blob([payload], { type: 'application/json' })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sessionId, location.pathname]);
};

export default usePageTracking;
